import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Table } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const PermissionsTable = ({ groupedPermissions, rolePermissions, roleName }) => {
  const { t } = useTranslation();

  // Standard action columns
  const ACTION_COLUMNS = useMemo(() => [
    { key: "view", label: t('shared.view', 'View'), actions: ["read", "view"] },
    { key: "create", label: t('shared.create', 'Create'), actions: ["create"] },
    { key: "edit", label: t('shared.edit', 'Edit'), actions: ["update", "manage"] },
    { key: "delete", label: t('shared.delete', 'Delete'), actions: ["delete"] },
  ], [t]);

  // Modules to display with their descriptions
  const MODULE_CONFIG = useMemo(() => ({
    asset: {
      displayName: t('shared.assets', 'Assets'),
      description: t('business.components.permissions_table.modules.asset_desc', 'Manage equipment and devices'),
    },
    "asset-issue": {
      displayName: t('shared.work_orders', 'Work Orders'),
      description: t('business.components.permissions_table.modules.asset_issue_desc', 'Track and manage service requests'),
    },
    location: {
      displayName: t('shared.locations', 'Locations'),
      description: t('business.components.permissions_table.modules.location_desc', 'Manage facility locations'),
    },
    part: {
      displayName: t('shared.parts', 'Parts'),
      description: t('business.components.permissions_table.modules.part_desc', 'Manage spare parts inventory'),
    },
    "service-contract": {
      displayName: t('shared.service_contracts', 'Service Contracts'),
      description: t('business.components.permissions_table.modules.service_contract_desc', 'Manage vendor service agreements'),
    },
    vendor: {
      displayName: t('shared.vendor', 'Vendor'),
      description: t('business.components.permissions_table.modules.vendor_desc', 'Manage vendor information'),
    },
    user: {
      displayName: t('shared.users', 'Users'),
      description: t('business.components.permissions_table.modules.user_desc', 'Manage user accounts'),
    },
    organization: {
      displayName: t('shared.organization', 'Organization'),
      description: t('business.components.permissions_table.modules.organization_desc', 'Manage organization settings'),
    },
  }), [t]);

  // Ownership restrictions by role and module
  const OWNERSHIP_RESTRICTIONS = {
    ROLE_FULL_USER: {
      asset: { delete: true },
      "asset-issue": { delete: true },
      location: { delete: true },
      part: { delete: true },
      "service-contract": { delete: true },
      vendor: { delete: true },
    },
    ROLE_REQUESTOR: {
      "asset-issue": { edit: true, delete: true },
    },
  };

  const ALLOWED_MODULES = useMemo(() => Object.keys(MODULE_CONFIG), [MODULE_CONFIG]);
  // Filter and order groups based on MODULE_CONFIG
  const visibleGroups = useMemo(() => {
    return ALLOWED_MODULES
      .map((resource) => groupedPermissions.find((g) => g.resource === resource))
      .filter((group) => group && group.permissions?.some((p) => rolePermissions.has(p.name)));
  }, [groupedPermissions, rolePermissions]);

  // Check if a permission exists for given action column
  const hasPermissionForAction = (group, actionColumn) => {
    return group.permissions?.some(
      (p) => actionColumn.actions.includes(p.action) && rolePermissions.has(p.name)
    );
  };

  // Check if action has ownership restriction
  const hasOwnershipRestriction = (resource, actionKey) => {
    const roleRestrictions = OWNERSHIP_RESTRICTIONS[roleName];
    if (!roleRestrictions) return false;
    const moduleRestrictions = roleRestrictions[resource];
    if (!moduleRestrictions) return false;
    return moduleRestrictions[actionKey] === true;
  };

  if (visibleGroups.length === 0) {
    return (
      <div className="text-center py-3 text-muted">
        {t('business.components.permissions_table.no_permissions', 'No permissions assigned')}
      </div>
    );
  }

  return (
    <Table responsive className="mb-0" aria-label={t('business.components.permissions_table.aria_label', 'Role permissions matrix')}>
      <thead>
        <tr style={{ backgroundColor: "#f8f9fa" }}>
          <th scope="col" className="text-muted fw-normal" style={{ minWidth: "220px" }}>
            {t('shared.module', 'Module')}
          </th>
          {ACTION_COLUMNS.map((action) => (
            <th
              scope="col"
              key={action.key}
              className="text-center text-muted fw-normal"
              style={{ width: "100px" }}
            >
              {action.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {visibleGroups.map((group) => {
          const config = MODULE_CONFIG[group.resource] || {};
          return (
            <tr key={group.resource}>
              <td className="py-3">
                <div className="fw-semibold">
                  {config.displayName || group.resourceDisplayName || group.resource}
                </div>
                <small className="text-muted">{config.description}</small>
              </td>
              {ACTION_COLUMNS.map((actionColumn) => {
                const hasPermission = hasPermissionForAction(group, actionColumn);
                const isRestricted = hasOwnershipRestriction(group.resource, actionColumn.key);

                return (
                  <td key={actionColumn.key} className="text-center align-middle">
                    {hasPermission ? (
                      <div className="d-flex flex-column align-items-center">
                        <span
                          className="d-inline-flex align-items-center justify-content-center border border-success rounded"
                          style={{ width: 24, height: 24, borderWidth: "2px" }}
                        >
                          <FeatherIcon
                            icon="check"
                            size={16}
                            className="text-success"
                          />
                        </span>
                        {isRestricted && (
                          <small className="text-muted" style={{ fontSize: "0.65rem", marginTop: "2px" }}>
                            {t('business.components.permissions_table.own_only', 'Own only')}
                          </small>
                        )}
                      </div>
                    ) : (
                      <span
                        className="d-inline-flex align-items-center justify-content-center border rounded"
                        style={{ width: 24, height: 24, borderColor: "#dee2e6" }}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default PermissionsTable;
